import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import VideoJs from "./player/VideoJs";
import videojs from "video.js";
import SideMenu from "./SideMenu";
import CommentArea from "./chat/CommentArea";
import ChatModal from "./chat/ChatInputModal";
import {
  CommentModel,
  CommentsConfig,
  Data,
  PlayerMeta,
  PlayerStatus,
  ProductModel,
  PusherConfig,
  VideoJsOptions,
  VideoSource,
  StreamerInfo,
} from "@/types";
import Pusher from "pusher-js";
import Modal from "react-modal";
import Header from "./Header";
import { usePlayer } from "@/lib/PlayerContext";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import ShopModal from "./shop/ShopModal";
import ProductList from "./shop/ProductList";
import CommentList from "./chat/CommentView";
import SubtitleView from "./SubtitleView";
import { useAnalytics } from "@/lib/AnalyticsContext";
import BannerContainer from "./banner/BannerContainer";
import { useCommentScroll } from "@/hooks/useCommentScroll";
import { filterCommentsByTime } from "@/utils/commentFilter";
import {
  parseTranslateData,
  getMessageByLanguage,
  applyTranslationToComment,
} from "@/utils/commentTranslation";
import { FaEye, FaEyeSlash } from "react-icons/fa";

// プロモーション動画対応のsrcUrl型
export type SrcUrlConfig = string | { promotion: string; main: string };

export interface PlayerProps {
  id: string;
  onSetup: (id: string) => Promise<{
    meta: PlayerMeta;
    title?: string;
    views?: number | null;
    srcUrl: SrcUrlConfig;
    products: ProductModel[];
    thumbnail?: string;
    startAt?: string;
    comments: CommentsConfig;
    pusher?: PusherConfig;
    status: PlayerStatus;
    privacyPolicyUrl?: string;
    gaId?: string;
    fullScreen?: boolean;
    startFullScreen?: boolean;
    streamer?: StreamerInfo;
    subtitleUrls?: { [lang: string]: string };
    landscape?: boolean;
    onAirByStream?: boolean;
    banner?: {
      testMode?: boolean;
    };
  }>;
  onCommentSend: (
    nickname: string,
    message: string,
    meta: PlayerMeta
  ) => Promise<boolean | void>;
  onCommentRecieve: (data: Data) => Promise<CommentModel>;
  onStart: (meta: PlayerMeta) => Promise<void>;
  onFullScreen?: (fullScreen: boolean) => Promise<void>;
  onHeartSend: (meta: PlayerMeta) => Promise<void>;
  onReady: (meta: PlayerMeta) => Promise<PlayerStatus>;
}

// グローバルな音声制御のための静的変数
const activePlayerIds = new Set<string>();

// ミニプレイヤーの自動再生がブラウザポリシーによりブロックされたことを検知した場合のフラグ
// （各プレイヤーインスタンスごとに管理するため、useRefに移動）

function Player({
  id = "",
  onSetup,
  onCommentSend,
  onCommentRecieve,
  onHeartSend,
  onStart,
  onFullScreen,
}: PlayerProps) {
  const {
    meta,
    setMeta,
    title,
    setTitle,
    views,
    setViews,
    products,
    setProducts,
    startAt,
    setStartAt,
    thumbnail,
    setThumbnail,
    status,
    setStatus,
    pusher,
    setPusher,
    pusherConfig,
    setPusherConfig,
    comments,
    setComments,
    allComments,
    setAllComments,
    commentScrollEnabled,
    setCommentScrollEnabled,
    filteredComments,
    setFilteredComments,
    showComment,
    setShowComment,
    showSubtitle,
    setShowSubtitle,
    isPlay,
    setIsPlay,
    fullScreen,
    setFullScreen,
    setIsMuted,
    pinnedComment,
    setPinnedComment,
  } = usePlayer();

  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { registerPlayer } = useCommentScroll();

  const isLoading = useMemo(() => status === "loading", [status]);
  const [sources, setSources] = useState<VideoSource[]>([]);
  const [subtitleUrls, setSubtitleUrls] = useState<
    { [lang: string]: string } | undefined
  >(undefined);

  const [hideFullScreen, setHideFullScreen] = useState<boolean>(false);
  const [startFullScreen, setStartFullScreen] = useState<boolean>(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const initialScrollRef = useRef({ top: 0, left: 0 });
  const playerInstanceRef = useRef<any>(null);
  const shouldRemainMutedRef = useRef(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const autoPlayAttemptedRef = useRef(false);
  const miniAutoPlayDisabledRef = useRef(false);
  const [autoDismissPlayReadyModal, setAutoDismissPlayReadyModal] =
    useState(false);

  const [isOpenChatModal, setIsOpenChatModal] = useState<boolean>(false);
  const [isSendingChat, setIsSendingChat] = useState<boolean>(false);
  const [nickname, setNickname] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isOpenShopModal, setIsOpenShopModal] = useState<boolean>(false);
  const [isPlayReadyModalOpen, setIsPlayReadyModalOpen] =
    useState<boolean>(true);

  const showCommentWithPlayerStatus = useMemo(() => {
    if (status === "onAir" && !isPlay) return false;
    if (isPlayReadyModalOpen) return false;
    else return showComment;
  }, [status, isPlay, showComment, isPlayReadyModalOpen]);
  const [lastViewCountTimestamp, setLastViewCountTimestamp] =
    useState<string>("");
  const [streamer, setStreamer] = useState<StreamerInfo | undefined>();
  const [selectedProduct, setSelectedProduct] = useState<ProductModel | null>(
    null
  );

  const [showProductList, setShowProductList] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [bannerConfig, setBannerConfig] = useState<
    { testMode?: boolean } | undefined
  >(undefined);
  const [commentStateBeforeShare, setCommentStateBeforeShare] = useState<
    boolean | null
  >(null);
  const [landscape, setLandscape] = useState<boolean>(false);

  // コメント表示制御：ユーザー上書きと管理側状態
  const [userCommentOverride, setUserCommentOverride] = useState<
    boolean | null
  >(null);
  const userOverrideRef = useRef<boolean | null>(userCommentOverride);
  const m3u8PollingActiveRef = useRef<boolean | null>(null);
  const [lastStreamerVisible, setLastStreamerVisible] = useState<boolean>(true);
  
  const commentsRef = useRef<CommentModel[]>([]);
  const allCommentsRef = useRef<CommentModel[]>([]);

  // プロモーション動画関連
  const [isPlayingPromotion, setIsPlayingPromotion] = useState<boolean>(false);
  const [mainSrcUrl, setMainSrcUrl] = useState<string>("");
  const receivedStartEventRef = useRef<boolean>(false);
  // クロージャ対策用のref
  const isPlayingPromotionRef = useRef<boolean>(false);
  const mainSrcUrlRef = useRef<string>("");
  const fullScreenRef = useRef<boolean>(fullScreen);

  useEffect(() => {
    isPlayingPromotionRef.current = isPlayingPromotion;
  }, [isPlayingPromotion]);

  useEffect(() => {
    mainSrcUrlRef.current = mainSrcUrl;
  }, [mainSrcUrl]);

  useEffect(() => {
    fullScreenRef.current = fullScreen;
  }, [fullScreen]);

  // フルスクリーン時に祖先の containing block / overflow を一時解除
  interface SavedAncestorStyle {
    el: HTMLElement;
    willChange: string;
    overflow: string;
    overflowX: string;
    transform: string;
    transition: string;
    webkitOverflowScrolling: string;
  }

  const savedAncestorStylesRef = useRef<SavedAncestorStyle[]>([]);
  const ancestorObserverRef = useRef<MutationObserver | null>(null);
  useEffect(() => {
    const modalEl = document.getElementById(`player-modal-${id}`);
    if (!modalEl) return;

    if (fullScreen) {
      const saved: SavedAncestorStyle[] = [];
      let el: HTMLElement | null = modalEl.parentElement;
      while (el && el !== document.body) {
        const cs = window.getComputedStyle(el);
        const isCarouselTrack = el.classList.contains("fp-carousel-track");
        const isCarouselWrapper = el.classList.contains("fp-carousel-wrapper");
        const isPlayerArea = el.classList.contains("fp-player-area");
        const needsFix =
          cs.willChange === "transform" ||
          cs.transform !== "none" ||
          (cs.overflow !== "visible" && isCarouselWrapper);
        if (needsFix || isPlayerArea || isCarouselTrack) {
          saved.push({
            el,
            willChange: el.style.willChange,
            overflow: el.style.overflow,
            overflowX: el.style.overflowX,
            transform: el.style.transform,
            transition: el.style.transition,
            webkitOverflowScrolling: (el.style as any).webkitOverflowScrolling || "",
          });
          if (cs.willChange === "transform") el.style.willChange = "auto";
          if (cs.transform !== "none") el.style.transform = "none";
          if (isCarouselWrapper || isPlayerArea) {
            el.style.overflow = "visible";
          }
          if (isCarouselTrack) {
            el.style.transform = "none";
            el.style.overflow = "visible";
            el.style.overflowX = "visible";
            el.style.transition = "none";
            (el.style as any).webkitOverflowScrolling = "auto";
          }
        }
        el = el.parentElement;
      }
      savedAncestorStylesRef.current = saved;

      // MutationObserver で祖先のtransform再追加を検知・防止
      if (ancestorObserverRef.current) {
        ancestorObserverRef.current.disconnect();
      }
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === "attributes" && mutation.attributeName === "style") {
            const target = mutation.target as HTMLElement;
            if (target.classList.contains("fp-carousel-track")) {
              const cs2 = window.getComputedStyle(target);
              if (cs2.transform !== "none") {
                target.style.transform = "none";
              }
            }
          }
        }
      });
      for (const { el: savedEl } of saved) {
        observer.observe(savedEl, { attributes: true, attributeFilter: ["style"] });
      }
      ancestorObserverRef.current = observer;
    } else {
      if (ancestorObserverRef.current) {
        ancestorObserverRef.current.disconnect();
        ancestorObserverRef.current = null;
      }
      for (const s of savedAncestorStylesRef.current) {
        s.el.style.willChange = s.willChange;
        s.el.style.overflow = s.overflow;
        s.el.style.overflowX = s.overflowX;
        s.el.style.transform = s.transform;
        s.el.style.transition = s.transition;
        (s.el.style as any).webkitOverflowScrolling = s.webkitOverflowScrolling;
      }
      savedAncestorStylesRef.current = [];
    }
  }, [fullScreen, id]);

  useEffect(() => {
    userOverrideRef.current = userCommentOverride;
  }, [userCommentOverride]);

  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  useEffect(() => {
    allCommentsRef.current = allComments;
  }, [allComments]);

  const subtitleOverlayVisible = useMemo(() => {
    return (
      fullScreen && !isLanguageMenuOpen && !isOpenShopModal && !isOpenChatModal
    );
  }, [fullScreen, isLanguageMenuOpen, isOpenShopModal, isOpenChatModal]);

  // スマホ用の高さ計算関数
  const getPlayerHeight = () => {
    if (!fullScreen) return "100%";

    if (isMobile) {
      // スマホの場合: 100svh
      return "calc(100svh)";
    } else {
      // PC/タブレットの場合: 既存の計算
      return "calc(100vh - 48px)";
    }
  };

  useEffect(() => {
    initialScrollRef.current = {
      top: window.scrollY,
      left: window.scrollX,
    };
  }, []);

  useEffect(() => {
    autoPlayAttemptedRef.current = false;
    miniAutoPlayDisabledRef.current = false;
    setAutoDismissPlayReadyModal(false);
    setIsPlayerReady(false);
    setIsPlayReadyModalOpen(true);
    shouldRemainMutedRef.current = false;
    // プロモーション動画関連のリセット
    receivedStartEventRef.current = false;
    setIsPlayingPromotion(false);
    setMainSrcUrl("");
  }, [id]);

  // スマホ判定
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice =
        /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(
          userAgent
        );
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const resetChatState = () => {
      setIsOpenChatModal(false);
      setNickname("");
      setMessage("");
    };

    resetChatState();

    // ステータスがonAirでない場合、またはプレイしていない場合はチャットを無効化
    if (status !== "onAir" || !isPlay) {
      resetChatState();
    }
  }, [id, status, isPlay]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsOpenChatModal(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleVideoStart = useCallback(async () => {
    setIsPlayReadyModalOpen(false);
    const startPosition = {
      top: window.scrollY,
      left: window.scrollX,
    };
    setPosition(startPosition);

    const preventScrollDuringStart = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      window.scrollTo(startPosition.left, startPosition.top);
    };

    window.addEventListener("scroll", preventScrollDuringStart, {
      passive: false,
    });

    try {
      await onStart(meta);
      setIsPlay(true);
      setShowSubtitle(true);
      analytics.sendLiveCommerceEvent.playStart(meta.id);

      if (startFullScreen) {
        setFullScreen(true);
        onFullScreen && onFullScreen(true);
      }
    } finally {
      window.removeEventListener("scroll", preventScrollDuringStart);

      requestAnimationFrame(() => {
        window.scrollTo({
          top: startPosition.top,
          left: startPosition.left,
          behavior: "instant" as ScrollBehavior,
        });
      });
    }
  }, [
    analytics,
    meta,
    onFullScreen,
    onStart,
    setFullScreen,
    setIsPlay,
    setIsPlayReadyModalOpen,
    setPosition,
    setShowSubtitle,
    startFullScreen,
  ]);

  // プレイヤーのインスタンスを保存
  const handlePlayerReady = (player: any) => {
    playerInstanceRef.current = player;
    setIsPlayerReady(true);

    // 自動再生中はミュート維持フラグをリセットしない
    if (!autoPlayAttemptedRef.current) {
      shouldRemainMutedRef.current = false;
    }

    if (fullScreenRef.current) {
      player.muted(false);
      setIsMuted(false);
    } else {
      player.muted(true);
      setIsMuted(true);
    }

    // プレイヤーのイベントリスナー
    player.on("play", () => {
      handlePlayerPlay();
    });

    player.on("pause", () => {
      handlePlayerPause();
    });

    // ミュート状態の変更を監視
    player.on("volumechange", () => {
      if (playerInstanceRef.current) {
        const currentMuted = playerInstanceRef.current.muted();
        setIsMuted(currentMuted);
        if (!currentMuted && shouldRemainMutedRef.current) {
          shouldRemainMutedRef.current = false;
          unmuteCurrentPlayer();
        }
      }
    });

    // プロモーション動画終了時の本編切替処理
    player.on("ended", () => {
      // refを参照してクロージャ問題を回避
      if (!isPlayingPromotionRef.current || !mainSrcUrlRef.current) return;

      const nextMainUrl = mainSrcUrlRef.current;

      // React stateを更新
      setIsPlayingPromotion(false);
      setMainSrcUrl("");

      const nextSources = [
        { src: nextMainUrl, type: "application/vnd.apple.mpegurl" },
      ];

      // 同一playerでソースを切り替えて即再生（setTimeoutを避ける）
      try {
        player.src(nextSources as any);
        player.play();
      } catch {
        // no-op
      }

      // React側も追従
      setSources(nextSources);
    });

    // コメントスクロール機能にプレーヤーを登録
    registerPlayer(player);

    // コメントスクロール機能の初期化（少し遅延させて確実に実行）
    setTimeout(() => {
      if (commentScrollEnabled && allComments.length > 0 && startAt) {
        // 初期コメントフィルタリング
        const currentTime = player.currentTime() || 0;
        const filtered = filterCommentsByTime(
          allComments,
          startAt,
          currentTime
        );
        setFilteredComments(filtered);
        setComments(filtered);
      }
    }, 1000);
  };

  const unmuteCurrentPlayer = useCallback(() => {
    if (!playerInstanceRef.current) return;
    shouldRemainMutedRef.current = false;

    const allPlayers = videojs.getPlayers() as Record<string, any>;
    const currentPlayerInstance = playerInstanceRef.current;

    try {
      Object.entries(allPlayers).forEach(([playerId, rawPlayer]) => {
        if (!rawPlayer) return;
        const otherPlayer = rawPlayer as any;
        const isCurrentPlayer = otherPlayer === currentPlayerInstance;
        const otherVideoEl = otherPlayer.tech_?.el_;
        const otherEpisodeId = otherVideoEl?.getAttribute?.("data-foove-player-id");
        const isSameEpisode = otherEpisodeId === id;
        if (isCurrentPlayer || isSameEpisode) {
          return;
        }

        // 他のプレイヤーは再生を停止し、ミュート状態にする
        try {
          otherPlayer.pause();
        } catch {
          // no-op
        }
        try {
          otherPlayer.muted(true);
        } catch {
          // no-op
        }

        const event = new CustomEvent("player-muted", {
          detail: {
            playerId: playerId.replace("player-", ""),
            muted: true,
          },
          bubbles: true,
        });
        window.dispatchEvent(event);
      });
    } catch {
      // no-op
    }

    playerInstanceRef.current.muted(false);
    setIsMuted(false);
    const event = new CustomEvent("player-muted", {
      detail: {
        playerId: id,
        muted: false,
      },
      bubbles: true,
    });
    window.dispatchEvent(event);
  }, [id, setIsMuted]);

  // プレイヤー再生時の処理
  const handlePlayerPlay = useCallback(() => {
    if (!playerInstanceRef.current) return;

    if (!shouldRemainMutedRef.current) {
      unmuteCurrentPlayer();
    }

    // activePlayerIdsを更新
    activePlayerIds.add(id);
  }, [id, unmuteCurrentPlayer]);

  // ミュート状態変更イベントのリスナー
  useEffect(() => {
    const handlePlayerMuted = (event: any) => {
      const { playerId, muted } = event.detail;
      if (playerId === id && playerInstanceRef.current) {
        if (!muted && !fullScreenRef.current) return;
        playerInstanceRef.current.muted(muted);
        setIsMuted(muted);
      }
    };

    window.addEventListener("player-muted", handlePlayerMuted);
    return () => {
      window.removeEventListener("player-muted", handlePlayerMuted);
    };
  }, [id]);

  // プレイヤー停止時の処理
  const handlePlayerPause = useCallback(() => {
    if (playerInstanceRef.current && playerInstanceRef.current.paused()) {
      activePlayerIds.delete(id);
      // 停止時のミュート状態を保持
      const isMuted = playerInstanceRef.current.muted();
      setIsMuted(isMuted);
    }
  }, [id]);

  const shouldAttemptMiniAutoPlay = useMemo(() => {
    return (
      status === "onAir" &&
      !fullScreen &&
      !startFullScreen &&
      sources.length > 0 &&
      !isPlay
    );
  }, [status, fullScreen, startFullScreen, sources.length, isPlay]);

  useEffect(() => {
    if (
      !shouldAttemptMiniAutoPlay ||
      autoPlayAttemptedRef.current ||
      !isPlayerReady ||
      !playerInstanceRef.current ||
      miniAutoPlayDisabledRef.current
    ) {
      return;
    }

    autoPlayAttemptedRef.current = true;

    const attemptAutoPlay = async () => {
      const player = playerInstanceRef.current;
      if (!player) return;

      try {
        shouldRemainMutedRef.current = true;
        player.muted(true);
        await player.play();
        setAutoDismissPlayReadyModal(true);
        await handleVideoStart();
      } catch (error) {
        console.warn("Mini player autoplay failed:", error);

        // このプレイヤーインスタンスでの自動再生を無効化
        miniAutoPlayDisabledRef.current = true;
        try {
          player.pause();
        } catch {
          // no-op
        }

        // 外部（setup.js等）に自動再生失敗を通知
        window.dispatchEvent(new CustomEvent("fp-autoplay-failed", { detail: { playerId: id } }));
      }
    };

    attemptAutoPlay();
  }, [handleVideoStart, isPlayerReady, shouldAttemptMiniAutoPlay]);

  // シェアモーダル開閉時の処理
  const handleShareModalChange = useCallback(
    (isOpen: boolean) => {
      setIsShareModalOpen(isOpen);

      if (isOpen) {
        // シェアモーダルを開く時：現在のコメント表示状態を保存して非表示にする
        setCommentStateBeforeShare(showComment);
        if (showComment) {
          setShowComment(false);
        }
      } else {
        // シェアモーダルを閉じる時：元々表示されていた場合のみ再表示する
        if (commentStateBeforeShare === true) {
          setShowComment(true);
        }
        setCommentStateBeforeShare(null);
      }
    },
    [showComment, commentStateBeforeShare, setShowComment]
  );

  // 言語切替時にコメントを再マッピング
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      // 表示中のコメントを更新
      setComments((prevComments) =>
        prevComments.map((comment) => applyTranslationToComment(comment, lng))
      );

      // allCommentsも更新
      setAllComments((prevComments) =>
        prevComments.map((comment) => applyTranslationToComment(comment, lng))
      );

      // フィルタリングされたコメントも更新
      setFilteredComments((prevComments) =>
        prevComments.map((comment) => applyTranslationToComment(comment, lng))
      );

      // ピン留めコメントも更新
      if (pinnedComment) {
        setPinnedComment(applyTranslationToComment(pinnedComment, lng));
      }
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [setComments, setAllComments, setFilteredComments, pinnedComment, setPinnedComment]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      activePlayerIds.delete(id);
      if (playerInstanceRef.current) {
        playerInstanceRef.current.dispose();
        playerInstanceRef.current = null;
      }
      setIsPlayerReady(false);
    };
  }, [id]);

  // VideoJsのオプションを拡張
  // autoplayは初期化時のみ参照される設定のため、fullScreenの変更で
  // options参照が変わりVideoJsのuseEffectが再発火するのを防ぐ
  const videoJsOptions = useMemo(
    () =>
      ({
        options: {
          autoplay: !startFullScreen,
          controls: true,
          fluid: false,
          fill: true,
          responsive: true,
          aspectratio: "16:9",
          bigPlayButton: false,
          playsinline: true,
          muted: true,
          userActions: {
            click: false,
            doubleClick: false,
          },
          inactivityTimeout: 0,
          suppressScrolling: true,
        },
        sources: [],
      }) as VideoJsOptions,
    [startFullScreen]
  );
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState<string>("");

  // m3u8ファイルの先頭が#EXTM3Uかチェック（5秒タイムアウト）
  const checkM3u8Ready = async (
    url: string,
    timeoutMs = 5000
  ): Promise<boolean> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        cache: "no-store",
        signal: controller.signal,
      });
      if (!res.ok) return false;
      const text = await res.text();
      return text.startsWith("#EXTM3U");
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  };

  // m3u8が準備完了するまでポーリング（10秒間隔、最大240回）
  const waitUntilM3u8Ready = async (
    url: string,
    options = { intervalMs: 10000, timeoutMs: 5000, maxAttempts: 240 },
    shouldContinue: () => boolean | null
  ): Promise<void> => {
    for (let i = 0; i < options.maxAttempts && shouldContinue(); i++) {
      const ok = await checkM3u8Ready(url, options.timeoutMs);
      if (ok) return; // 成功時にresolve
      await new Promise((r) => setTimeout(r, options.intervalMs));
    }
    throw new Error("m3u8 not ready"); // 失敗でreject
  };

  // Promiseにタイムアウトを追加
  const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> => {
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("timeout")), ms);
      p.then((v) => {
        clearTimeout(t);
        resolve(v);
      }).catch((e) => {
        clearTimeout(t);
        reject(e);
      });
    });
  };

  const readyPromise = (pusher: Pusher, prefix: string): Promise<boolean> => {
    const channelName = prefix + "viewer-channel";
    let channel = pusher.channel(channelName);
    if (!channel) {
      channel = pusher.subscribe(channelName);
    }
    const waitTime = 5000; // waitTimeを5秒に固定
    return new Promise((resolve) => {
      channel.bind(prefix + "live-start-event", async (data: any) => {
        // start-event受信フラグを設定（プロモーション動画スキップ用）
        receivedStartEventRef.current = true;
        setTimeout(() => resolve(true), waitTime);
      });
    });
  };

  const setupPlayer = async () => {
    try {
      console.log("Setting up player with id:", id);
      const res = await onSetup(id);
      console.log("Setup response:", res);
      setTitle(res?.title ?? "");
      setViews(res?.views ?? null);
      setProducts(res?.products ?? []);
      setStartAt(res?.startAt ?? "");
      setThumbnail(res?.thumbnail ?? "");
      setPrivacyPolicyUrl(res?.privacyPolicyUrl ?? "");
      setStreamer(res?.streamer);
      setSubtitleUrls(res?.subtitleUrls);
      setLandscape(!!res?.landscape);
      setBannerConfig(res?.banner);
      setShowSubtitle(false);
      if (res?.fullScreen) {
        setFullScreen(res?.fullScreen ?? false);
        res?.fullScreen &&
          onFullScreen &&
          onFullScreen(res?.fullScreen ?? false);
        setHideFullScreen(true);
      }
      if (res?.startFullScreen) {
        setStartFullScreen(res?.startFullScreen ?? true);
      }
      if (res?.gaId) {
        analytics.setGaId(res.gaId);
        // meta.userIdが存在する場合のみ設定（存在しない場合はGAのClient IDから自動生成される）
        if (meta.userId) {
          analytics.setUserId(meta.userId);
        }
        analytics.setEpisodeId(id);
      }
      let currentStatus = res?.status;
      let newPusher: Pusher | null = null;
      if (res?.pusher) {
        newPusher = new Pusher(res.pusher.key, {
          cluster: res.pusher.cluster,
        });
        const prefix = res.pusher.prefix + "-";
        const channelName = prefix + "viewer-channel";
        let channel = newPusher.channel(channelName);
        if (!channel) {
          channel = newPusher.subscribe(channelName);
        }
        channel.bind(prefix + "chat-event", async (data: any) => {
          const newComment = await onCommentRecieve(data);

          // 翻訳データがある場合は処理
          if (data.translate) {
            const translationData = parseTranslateData(data.translate);
            if (translationData) {
              newComment.translations = translationData.translations;
              newComment.originalLanguage = translationData.originalLanguage;
              newComment.originalMessage = newComment.message;

              // 現在の言語に応じたメッセージを設定
              const currentLang = i18n.language;
              newComment.message = getMessageByLanguage(
                newComment,
                currentLang
              );
            }
          }

          setComments((prevComments: CommentModel[]) => [
            ...prevComments,
            newComment,
          ]);
        });
        channel.bind(prefix + "chat-delete-event", async (data: any) => {
          setComments((prevComments: CommentModel[]) =>
            prevComments.filter((comment) => comment.id !== data.id)
          );
        });
        channel.bind(prefix + "live-close-event", async (data: any) => {
          setTimeout(() => setStatus("end"), 20000);
        });
        channel.bind(prefix + "view-count-event", async (data: any) => {
          // timestampがある場合は古いデータかチェック
          if (data.timestamp) {
            const newTimestamp = data.timestamp;
            const newTimestampDate = new Date(newTimestamp).getTime();

            // 新しいtimestampの場合のみ更新
            if (
              !lastViewCountTimestamp ||
              newTimestampDate > new Date(lastViewCountTimestamp).getTime()
            ) {
              setViews(data.views ?? 0);
              setLastViewCountTimestamp(newTimestamp);
            }
          } else {
            // timestampがない場合は従来通り更新
            setViews(data.views ?? 0);
          }
        });
        channel.bind(
          prefix + "comment-visibility-event",
          async (data: { visible: boolean }) => {
            setLastStreamerVisible(data.visible);

            // ユーザーが非表示設定している場合は無視
            if (userOverrideRef.current === false) return;

            // それ以外は管理イベントに従う
            if (
              userOverrideRef.current === null ||
              userOverrideRef.current === true
            ) {
              setShowComment(data.visible);
            }
          }
        );
        channel.bind(
          prefix + "chat-pin-event",
          async (rawData: { id: string; pinned: boolean } | string) => {
            // Pusherからのデータをパース（例外処理付き）
            let data: { id: string; pinned: boolean };
            try {
              data = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
            } catch (e) {
              console.warn("[chat-pin-event] Failed to parse data:", e);
              return;
            }

            if (data.pinned) {
              // コメント一覧からIDで検索（Refを使って最新の状態を参照）
              const targetComment =
                commentsRef.current.find((c) => c.id === data.id) ||
                allCommentsRef.current.find((c) => c.id === data.id);

              if (targetComment) {
                // 現在の言語に応じた翻訳を適用してピン留め
                const currentLang = i18n.language;
                const pinnedCommentWithTranslation = applyTranslationToComment(
                  { ...targetComment, pinned: true },
                  currentLang
                );
                setPinnedComment(pinnedCommentWithTranslation);
              } else {
                console.warn("[chat-pin-event] Comment not found with id:", data.id);
              }
            } else {
              // ピン留め解除
              setPinnedComment(null);
            }
          }
        );
        setPusherConfig(res.pusher);
        setPusher(newPusher);
      }
      setMeta(res?.meta ?? {});

      // コメント設定の処理
      let comments: CommentModel[] = [];
      let scrollEnabled = false;

      if (Array.isArray(res?.comments)) {
        // 従来の配列形式
        comments = res.comments;
        scrollEnabled = false;
      } else if (res?.comments && typeof res.comments === "object") {
        // 新しいオブジェクト形式
        comments = res.comments.data || [];
        scrollEnabled = res.comments.scroll || false;
      }

      // コメントに翻訳データがあれば処理して現在の言語に合わせる
      const currentLang = i18n.language;
      const processedComments = comments.map((comment) => {
        // 既に翻訳データがある場合はそのまま使用
        if (comment.translations) {
          return applyTranslationToComment(comment, currentLang);
        }

        // translate属性（JSON文字列やオブジェクト）がある場合はパース
        const rawTranslate = (comment as any).translate;
        if (rawTranslate) {
          const translationData = parseTranslateData(rawTranslate);
          if (translationData) {
            const updatedComment: CommentModel = {
              ...comment,
              translations: translationData.translations,
              originalLanguage: translationData.originalLanguage,
              originalMessage: comment.message,
            };
            return applyTranslationToComment(updatedComment, currentLang);
          }
        }

        return comment;
      });

      setAllComments(processedComments);
      setCommentScrollEnabled(scrollEnabled);

      // 初期コメントからピン留めされたコメントを検索
      const initialPinnedComment = processedComments.find((c) => c.pinned === true);
      if (initialPinnedComment) {
        setPinnedComment(initialPinnedComment);
      }

      if (scrollEnabled) {
        // スクロール機能が有効な場合は初期状態では空のコメント
        setComments([]);
        setFilteredComments([]);
      } else {
        // 従来通りの処理
        setComments(processedComments);
      }

      // console.log("res.status", res?.status); // ログ削減
      if (res?.status === "onAir" || res?.status === "end") {
        setStatus(res?.status);
        if (res?.status === "onAir") {
          // ユーザー上書きがあればそれを優先、なければ管理側の状態に従う
          const shouldShow =
            userOverrideRef.current !== null
              ? userOverrideRef.current
              : lastStreamerVisible;
          setShowComment(shouldShow);
        }
      } else {
        setStatus("ready");
        if (res.pusher && newPusher) {
          const prefix = res.pusher.prefix + "-";
          const isStremeCheck =
            Boolean(res?.onAirByStream) &&
            Boolean(res?.srcUrl) &&
            m3u8PollingActiveRef.current === null;
          if (isStremeCheck) {
            // onAirByStreamがtrueの場合、start-eventとm3u8準備完了をPromise.anyでレース
            m3u8PollingActiveRef.current = true;
            const p1 = readyPromise(newPusher, prefix);
            // m3u8チェック用のURLを取得（オブジェクト形式の場合はmain URLを使用）
            const m3u8CheckUrl = typeof res.srcUrl === "string" 
              ? res.srcUrl 
              : res.srcUrl.main;
            const p2 = waitUntilM3u8Ready(
              m3u8CheckUrl,
              { intervalMs: 10000, timeoutMs: 5000, maxAttempts: 240 },
              () => m3u8PollingActiveRef.current
            );
            try {
              await Promise.any([p1, p2]);
              setStatus("onAir");
              const isShowComment =
                userOverrideRef.current !== null
                  ? userOverrideRef.current
                  : lastStreamerVisible;
              setShowComment(isShowComment);
              currentStatus = "onAir";
            } catch (e) {
              console.error(
                "再生エラー: start-event も m3u8 も確認できませんでした",
                e
              );
            } finally {
              m3u8PollingActiveRef.current = false;
            }
          } else {
            // 従来通り、start-eventでonAirに遷移
            await readyPromise(newPusher, prefix);
            setStatus("onAir");
            // ユーザー上書きがあればそれを優先、なければ管理側の状態に従う
            const isShowComment =
              userOverrideRef.current !== null
                ? userOverrideRef.current
                : lastStreamerVisible;
            setShowComment(isShowComment);
            currentStatus = "onAir";
          }
        }
      }
      if (res?.srcUrl && currentStatus === "onAir") {
        const srcUrlConfig = res.srcUrl;

        if (typeof srcUrlConfig === "object" && srcUrlConfig.promotion && srcUrlConfig.main) {
          // srcUrlがオブジェクト形式の場合
          // start-eventを受信していない場合はプロモーション動画を再生
          if (!receivedStartEventRef.current) {
            setMainSrcUrl(srcUrlConfig.main);
            setIsPlayingPromotion(true);
            setSources([
              {
                src: srcUrlConfig.promotion,
                type: "application/vnd.apple.mpegurl",
              },
            ]);
          } else {
            // start-event受信後はプロモーションをスキップして本編を再生
            setSources([
              {
                src: srcUrlConfig.main,
                type: "application/vnd.apple.mpegurl",
              },
            ]);
          }
        } else {
          // 従来の文字列形式
          const url = typeof srcUrlConfig === "string" ? srcUrlConfig : srcUrlConfig.main;
          setSources([
            {
              src: url,
              type: "application/vnd.apple.mpegurl",
            },
          ]);
        }
      }
    } catch (error) {
      console.error("Error in setupPlayer:", error);
      setStatus("ready"); // フォールバック状態
    }
  };

  useEffect(() => {
    setupPlayer().catch((error) => {
      console.error("Failed to setup player:", error);
    });
  }, [id, onCommentRecieve, onSetup]);

  const handleSendMessage = async () => {
    if (nickname && message) {
      setIsSendingChat(true);
      try {
        const result = await onCommentSend(nickname, message, meta);
        if (result === false) {
          // 送信が拒否された場合、ローカルにコメントを表示
          const localComment: CommentModel = {
            id: `local-${Date.now()}`,
            name: nickname,
            message: message,
            backgroundColor: "none",
            createdAt: new Date().toISOString(),
          };
          setComments((prevComments: CommentModel[]) => [
            ...prevComments,
            localComment,
          ]);
        }
        setMessage("");
        setIsOpenChatModal(false);
      } finally {
        setIsSendingChat(false);
      }
    }
  };

  // コメント表示/非表示トグル
  const toggleCommentVisibility = () => {
    const nextValue = !showComment;
    setUserCommentOverride(nextValue);
    setShowComment(nextValue);
  };

  const [playerWidth, setPlayerWidth] = useState<number>(270);

  useEffect(() => {
    const updateWidth = () => {
      const playerElement = document.getElementById(`player-${id}`);
      if (playerElement) {
        const widthStyle = playerElement.style.width;
        const width = widthStyle
          ? parseInt(widthStyle, 10)
          : playerElement.offsetWidth;
        setPlayerWidth(width);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    const resizeObserver = new ResizeObserver(updateWidth);
    const playerElement = document.getElementById(`player-${id}`);
    if (playerElement) {
      resizeObserver.observe(playerElement);
    }

    return () => {
      window.removeEventListener("resize", updateWidth);
      resizeObserver.disconnect();
    };
  }, [id]);

  // レイアウト状態に応じて商品リスト表示を制御
  useEffect(() => {
    // 横画面のときは常に商品リストを出さない
    if (landscape) {
      setShowProductList(false);
      return;
    }

    // 画面幅を監視して商品リストの表示を制御
    const handleResize = () => {
      if (!fullScreen) {
        setShowProductList(false);
        return;
      }

      // フルスクリーン時はプレイヤー幅 + 商品リスト幅 + マージンを考慮
      // プレイヤー幅: (100vh - 48px) * 9/16
      // 商品リスト幅: 400px + マージン
      const viewportHeight = window.innerHeight;
      const playerWidth = (viewportHeight - 48) * (9 / 16);
      const productListWidth = 400;
      const margins = 48 + 48; // プレイヤー周りのマージン + 商品リスト周りのマージン
      const minRequiredWidth = playerWidth + productListWidth + margins;
      const shouldShow = window.innerWidth >= minRequiredWidth;
      setShowProductList(shouldShow);
    };

    // 初期表示とフルスクリーン切り替え時に即座に実行
    setTimeout(handleResize, 0);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [fullScreen, isMobile, landscape]);

  return (
    <>
      <div
        className={`tw-relative tw-bg-transparent ${landscape
          ? (isMobile ? "tw-h-[180px] tw-w-[320px] tw-aspect-[16/9]" : "tw-h-[270px] tw-w-[480px] tw-aspect-[16/9]")
          : (isMobile ? "tw-h-[320px] tw-w-[180px] tw-aspect-[9/16]" : "tw-h-[480px] tw-w-[270px] tw-aspect-[9/16]")
        }`}
        id={`player-${id}`}
      >
        <div
          id={`player-modal-${id}`}
          style={fullScreen ? {
            position: "fixed",
            inset: 0,
            zIndex: 50,
            background: "rgb(50, 50, 50)",
          } : undefined}
        ></div>
        <Modal
          isOpen={true}
          contentLabel="Player Modal"
          ariaHideApp={false}
          style={{
            overlay: {
              position: fullScreen ? "fixed" : "static",
              background: fullScreen ? "rgb(50, 50, 50)" : "rgba(0, 0, 0, 0)",
              zIndex: fullScreen ? 50 : "auto",
              display: fullScreen ? "flex" : "block",
              justifyContent: fullScreen ? "center" : "flex-start",
              alignItems: fullScreen ? "center" : "flex-start",
              padding: "0",
              width: "100%",
              height: "100%",
              minWidth:
                !landscape && fullScreen && !isMobile
                  ? "calc((100vh - 48px) * 9/16 + 48px)"
                  : "auto",
            },
            content: landscape
              ? {
                  // 横画面
                  position: "relative",
                  inset: fullScreen ? "auto" : "0 auto 0 50%",
                  transform: fullScreen ? "none" : "translateX(-50%)",
                  padding: "0px",
                  background: "rgba(0, 0, 0, 1)",
                  border: "none",
                  borderRadius: fullScreen
                    ? isMobile
                      ? "0px"
                      : "30px"
                    : "20px",
                  overflow: "hidden" as const,
                  aspectRatio: "16/9",
                  height: fullScreen ? "100%" : (isMobile ? "180px" : "270px"),
                  width: fullScreen ? "100%" : (isMobile ? "min(320px, calc(100vw - 40px))" : "min(480px, calc(100vw - 40px))"),
                  maxWidth: fullScreen
                    ? "100%"
                    : isMobile ? "min(320px, calc(100vw - 40px))" : "min(480px, calc(100vw - 40px))",
                  minWidth: fullScreen
                    ? "100%"
                    : isMobile ? "min(320px, calc(100vw - 40px))" : "min(480px, calc(100vw - 40px))",
                  minHeight: fullScreen ? "auto" : (isMobile ? "180px" : "270px"),
                  margin: "0",
                  right: "0",
                }
              : {
                  // 縦画面
                  position: "relative",
                  inset: fullScreen ? "auto" : "0 auto 0 50%",
                  transform: fullScreen ? "none" : "translateX(-50%)",
                  padding: "0px",
                  background: "rgba(0, 0, 0, 1)",
                  border: "none",
                  borderRadius: fullScreen
                    ? isMobile
                      ? "0px"
                      : "30px"
                    : "20px",
                  overflow: "hidden" as const,
                  aspectRatio: "9/16",
                  height: getPlayerHeight(),
                  width: fullScreen
                    ? showProductList && !isMobile
                      ? "calc((100vh - 48px) * 9/16)"
                      : isMobile
                        ? "100vw"
                        : "calc((100vh - 48px) * 9/16)"
                    : "100%",
                  maxWidth: fullScreen ? "none" : "calc(100vh * 9/16)",
                  minWidth: fullScreen
                    ? isMobile
                      ? "100vw"
                      : "calc((100vh - 48px) * 9/16)"
                    : "auto",
                  margin: "0",
                  right: showProductList && !isMobile ? "220px" : "0",
                },
          }}
          parentSelector={(): HTMLElement =>
            document.getElementById(`player-modal-${id}`) ?? document.createElement("div")
          }
        >
          <div
            className={`tw-h-full tw-w-full tw-relative tw-font-sans ${i18n.language === "zh" ? "tw-chinese-font" : ""}`}
          >
            {isLoading ? (
              <div className="tw-flex tw-justify-center tw-h-full tw-items-center">
                <div className="tw-animate-spin tw-h-10 tw-w-10 tw-border-4 tw-border-gray-500 tw-rounded-full tw-border-t-transparent tw-border-solid"></div>
                <span className="tw-ml-3">{t("player.loading")}</span>
              </div>
            ) : (
              <>
                <style>
                  {`
                    .video-js .vjs-big-play-button {
                      width: 80px !important;
                      height: 80px !important;
                      border-radius: 50% !important;
                      border: 3px solid white !important;
                      margin-left: -40px !important;
                      margin-top: -40px !important;
                      background-color: rgba(43, 51, 63, 0.7) !important;
                      transition: all 0.3s ease !important;
                    }
                    .video-js:hover .vjs-big-play-button {
                      background-color: rgba(43, 51, 63, 0.9) !important;
                      transform: scale(1.1) !important;
                    }
                  `}
                </style>
                <VideoJs
                  id={id}
                  sources={sources}
                  status={status}
                  startAt={startAt}
                  thumbnail={thumbnail}
                  options={videoJsOptions.options}
                  privacyPolicyUrl={privacyPolicyUrl}
                  subtitleUrls={subtitleUrls}
                  onReady={handlePlayerReady}
                  onStart={handleVideoStart}
                  dismissPlayReadyModal={autoDismissPlayReadyModal}
                  landscape={landscape}
                />
                {/* バナーコンテナ */}
                <BannerContainer
                  pusher={pusher}
                  episodeId={id}
                  userId={meta.userId || ""}
                  fullScreen={fullScreen}
                  testMode={bannerConfig?.testMode ?? false}
                />
              </>
            )}
          </div>
          <div className="tw-h-8 tw-w-full tw-absolute tw-top-0 tw-z-[30]">
            <Header
              title={title}
              views={views}
              pusher={pusher}
              pusherConfig={pusherConfig}
              streamer={streamer}
            ></Header>
          </div>
          <SideMenu
            id={id}
            products={products}
            fullScreen={fullScreen}
            landscape={landscape}
            isMobile={isMobile}
            hideFullScreen={hideFullScreen}
            onEnterFullScreen={() => {
              setFullScreen(true);
              onFullScreen && onFullScreen(true);
              unmuteCurrentPlayer();
              const currentScroll = {
                top: window.scrollY,
                left: window.scrollX,
              };
              setPosition(currentScroll);
              setTimeout(() => {
                window.scroll({
                  top: 0,
                  left: 0,
                  behavior: "instant" as ScrollBehavior,
                });
              }, 500);
            }}
            onExitFullScreen={() => {
              setFullScreen(false);
              onFullScreen && onFullScreen(false);
              setTimeout(() => {
                window.scroll({
                  ...position,
                  behavior: "instant" as ScrollBehavior,
                });
              }, 500);
            }}
            onCommentSend={onCommentSend}
            meta={meta}
            pusher={pusher}
            pusherConfig={pusherConfig}
            onHeartSend={onHeartSend}
            showChat={showComment}
            onTapChatMessageIcon={(status) => {
              setShowComment(status);
            }}
            onTapChatInputIcon={() => {
              setIsOpenChatModal(true);
            }}
            onTapShopIcon={() => setIsOpenShopModal(true)}
            hideShopIcon={fullScreen && showProductList}
            onLanguageMenuChange={setIsLanguageMenuOpen}
            onShareModalChange={handleShareModalChange}
            hideSideMenu={isOpenChatModal || isOpenShopModal}
          />
          {!isLanguageMenuOpen &&
            !isOpenShopModal &&
            !isOpenChatModal &&
            !fullScreen && (
              <div
                className={`tw-absolute tw-left-1 tw-top-1/2 tw-transform ${pinnedComment ? "tw-translate-y-0" : "tw-translate-y-1/4"} tw-w-2/3 tw-max-w-[400px] ${pinnedComment ? "tw-h-[40%]" : "tw-h-[30%]"} tw-z-10 tw-pointer-events-none`}
              >
                {showCommentWithPlayerStatus && (
                  <CommentArea fullScreen={fullScreen}></CommentArea>
                )}
              </div>
            )}
          {showSubtitle &&
            showCommentWithPlayerStatus &&
            !fullScreen &&
            !isLanguageMenuOpen &&
            !isOpenShopModal &&
            !isOpenChatModal && (
              <div className="tw-absolute tw-left-1/2 tw-bottom-6 tw-transform tw--translate-x-1/2 tw-w-[90%] tw-max-w-[600px] tw-z-[31] tw-pointer-events-none">
                <SubtitleView pusher={pusher} episodeId={id} />
              </div>
            )}
          <div id={`store-fulscreen-modal-${id}`} />
          <ChatModal
            open={isOpenChatModal}
            onClose={() => setIsOpenChatModal(false)}
            onSave={(nickname: string) => {
              setNickname(nickname);
            }}
            message={message}
            onMessageChange={setMessage}
            onSendMessage={handleSendMessage}
            hasNickname={!!nickname}
            currentNickname={nickname}
            fullScreen={fullScreen}
            playerWidth={playerWidth}
            playerId={id}
            isSending={isSendingChat}
          />
          <ShopModal
            open={isOpenShopModal}
            onClose={() => setIsOpenShopModal(false)}
            fullScreen={fullScreen}
            playerWidth={playerWidth}
            id={id}
            landscape={landscape}
            isMobile={isMobile}
          />
          {fullScreen &&
            showProductList &&
            !isMobile &&
            (() => {
              return (
                <div className="tw-fixed tw-right-6 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-[400px] tw-h-[calc(100vh-48px)] tw-bg-white/70 tw-backdrop-blur-sm tw-overflow-y-auto tw-rounded-2xl tw-z-[51]">
                  <ProductList
                    products={products}
                    selectedProduct={selectedProduct}
                    playerId={id}
                    onSelectProduct={(product) => {
                      setIsOpenShopModal(true);
                      setSelectedProduct(product);
                    }}
                  />
                </div>
              );
            })()}
          {fullScreen && (
            <div
              className={`
                tw-fixed tw-left-[40%] tw-bottom-28 tw-transform tw--translate-x-1/2 
                tw-w-[80%] tw-max-w-[800px] ${landscape ? (pinnedComment ? "tw-h-[240px]" : "tw-h-[120px]") : (pinnedComment ? "tw-h-[280px]" : "tw-h-[160px]")} tw-rounded-2xl tw-block tw-pointer-events-none
              `}
              style={{
                display: subtitleOverlayVisible ? "block" : "none",
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",
              }}
            >
              {showCommentWithPlayerStatus && comments.length > 0 ? (
                <CommentList comments={comments} />
              ) : (
                <CommentList comments={[]} />
              )}

              {isPlay && (
                <div
                  onClick={toggleCommentVisibility}
                  className={`tw-px-4 tw-text-left tw-bg-transparent tw-text-xs tw-text-white
                   tw-cursor-pointer tw-pointer-events-auto tw-flex tw-items-center tw-gap-1`}
                  style={{
                    textShadow:
                      "1px 1px 2px rgba(0, 0, 0, 0.5), -1px -1px 2px rgba(0, 0, 0, 0.5)",
                    filter: "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.8))",
                  }}
                >
                  {showComment ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
                  <span>{t("comment.title")}</span>
                </div>
              )}
            </div>
          )}

          {showSubtitle &&
            fullScreen &&
            !isLanguageMenuOpen &&
            !isOpenShopModal &&
            !isOpenChatModal && (
              <div
                className={`tw-fixed ${
                  showProductList && !isMobile ? "tw-left-[35%]" : "tw-left-1/2"
                } tw-bottom-16 tw-transform tw--translate-x-1/2 tw-w-[80%] tw-max-w-[800px] tw-pointer-events-none`}
              >
                <SubtitleView pusher={pusher} episodeId={id} />
              </div>
            )}
        </Modal>
      </div>
    </>
  );
}
export default Player;
